import {
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
  WritableSignal,
  inject,
  signal
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import { FinancialAdminControllerService } from 'src/app/admin-api/api/financialAdminController.service';
import { FinancialControllerService } from 'src/app/admin-api/api/financialController.service';
import { SubscriptionTypeControllerService } from 'src/app/admin-api/api/subscriptionTypeController.service';
import { AnualMonthlyPaymentConfig } from 'src/app/admin-api/model/anualMonthlyPaymentConfig';
import { Gateway } from 'src/app/admin-api/model/gateway';
import { SubscriptionAndSubscriptionTypeDto } from 'src/app/admin-api/model/subscriptionAndSubscriptionTypeDto';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';


@Component({
  selector: 'app-scheduler-jobs-config',
  templateUrl: './scheduler-jobs-config.component.html',
  styleUrls: ['./scheduler-jobs-config.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SchedulerJobsConfigComponent implements OnInit {
  private financialService = inject(FinancialControllerService);
  private financialAdminService = inject(FinancialAdminControllerService);
  private subscriptionTypeService = inject(SubscriptionTypeControllerService);
  private messageService = inject(MessageService);

  @Input({ required: true })
  permission: WritableSignal<boolean | undefined> = signal(false);

  anualMonthlyConfigs: AnualMonthlyPaymentConfig[] = [];
  gateways: Gateway[] = [];
  subscriptionTypes: SubscriptionAndSubscriptionTypeDto[] = [];
  gatewayOptions: { label: string; value: number }[] = [];
  subscriptionTypeNameMap: Map<number, string> = new Map();
  subscriptionNameMap: Map<number, string> = new Map();
  gatewayNameMap: Map<number, string> = new Map();
  anualMonthlyForm: FormGroup = new FormGroup({});
  pixRecurrenceScheduleDay: number | null = null;
  pixRecurrenceScheduleControl = new FormControl<number | null>(
    { value: null, disabled: true },
    [Validators.required, Validators.min(1), Validators.max(22)]
  );

  async ngOnInit(): Promise<void> {
    LoaderService.showLoader();
    try {
      this.applyPermissionState();
      await Promise.all([this.loadConfigs(), this.loadPixRecurrenceSchedule()]);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private normalizeSubscriptionLabel(value: unknown): string {
    if (value == null) {
      return '';
    }

    return String(value).replace('Anual.', 'Anual/mes');
  }

  async loadConfigs(): Promise<void> {
    const [anualMonthly, gateways, subscriptionTypes] = await Promise.all([
      lastValueFrom(
        this.financialService
          .findAnualMonthlyPaymentConfigs()
          .pipe(map((d) => d.result))
      ),
      lastValueFrom(
        this.financialService.findGatewaysList().pipe(map((d) => d.result))
      ),
      lastValueFrom(
        this.subscriptionTypeService
          .findAllSubscriptionByTypes()
          .pipe(map((d) => d.result))
      )
    ]);

    this.anualMonthlyConfigs = anualMonthly ?? [];
    this.gateways = gateways ?? [];
    this.subscriptionTypes = subscriptionTypes ?? [];

    this.gatewayOptions = this.gateways
      .filter((g) => g.gatewayId != null)
      .map((g) => ({
        label: g.gatewayName ?? String(g.gatewayId),
        value: g.gatewayId as number
      }));

    this.subscriptionTypeNameMap = new Map();
    this.subscriptionNameMap = new Map();
    for (const sub of this.subscriptionTypes) {
      for (const st of sub.subscriptionTypes ?? []) {
        if (st.subscriptionTypeId == null) {
          continue;
        }

        const typeName = this.normalizeSubscriptionLabel(
          st.subscriptionTypeName ?? st.subscriptionTypeId
        );
        const subName = this.normalizeSubscriptionLabel(
          sub.subscriptionName ?? sub.subscriptionId
        );
        this.subscriptionTypeNameMap.set(st.subscriptionTypeId, typeName);
        this.subscriptionNameMap.set(st.subscriptionTypeId, subName);
      }
    }

    this.buildAnualMonthlyForm();
  }

  buildAnualMonthlyForm(): void {
    const controls: { [key: string]: FormControl<number> } = {};

    for (const config of this.anualMonthlyConfigs) {
      if (config.configId == null) {
        continue;
      }

      controls[String(config.configId)] = new FormControl<number>(
        {
          value: parseInt(config.configValue ?? '0', 10),
          disabled: !this.permission()
        },
        [Validators.required, Validators.min(1), Validators.max(31)]
      );
    }

    this.anualMonthlyForm = new FormGroup(controls);

    if (!this.permission()) {
      this.anualMonthlyForm.disable();
    }
  }

  async saveAnualMonthlyConfigs(): Promise<void> {
    LoaderService.showLoader();
    try {
      for (const config of this.anualMonthlyConfigs) {
        if (config.configId == null) {
          continue;
        }

        const value: number = this.anualMonthlyForm.get(
          String(config.configId)
        )?.value;
        await lastValueFrom(
          this.financialAdminService
            .updateAnualMonthlyPaymentConfig(
              { configValue: String(value), label: config.label },
              config.configId
            )
            .pipe(map((d) => d.result))
        );
      }
      this.messageService.add({
        severity: 'success',
        summary: 'Salvo',
        detail: 'Configuracoes de cobranca anual/mes atualizadas.'
      });
      await this.loadConfigs();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async updatePixRecurrenceSchedule(): Promise<void> {
    if (!this.permission()) {
      return;
    }

    this.pixRecurrenceScheduleControl.markAsTouched();
    if (this.pixRecurrenceScheduleControl.invalid) {
      return;
    }

    const scheduleDay = this.pixRecurrenceScheduleControl.value;
    if (scheduleDay == null) {
      return;
    }

    LoaderService.showLoader();
    try {
      await lastValueFrom(
        this.financialAdminService.updatePixRecurrenceBotSchedule(scheduleDay)
      );
      this.pixRecurrenceScheduleDay = scheduleDay;
      this.messageService.add({
        severity: 'success',
        summary: 'Salvo',
        detail: 'Dia maximo de agendamento Pix atualizado.'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private applyPermissionState(): void {
    if (this.permission()) {
      this.pixRecurrenceScheduleControl.enable({ emitEvent: false });
      return;
    }

    this.pixRecurrenceScheduleControl.disable({ emitEvent: false });
  }

  private async loadPixRecurrenceSchedule(): Promise<void> {
    this.pixRecurrenceScheduleDay = await lastValueFrom(
      this.financialService
        .getLastPixRecurrenceScheduled()
        .pipe(map((data) => data.result as number))
    );
    this.pixRecurrenceScheduleControl.setValue(this.pixRecurrenceScheduleDay);
  }
}
